/** Fetch add-on products from the booking app's Supabase edge function. */

import { captureException, isFetchNetworkError } from "./insurance-tracking"

const EXCLUDE_IDS = new Set([
  "interpret-your-previous-blood-tests", // not relevant for new customers
  "full-body-mri", // mandatory, not an add-on
])

/** Map insurance-checker service IDs → Supabase package_types.
 *
 *  A membership tier is a bundled advanced blood test, so it takes the
 *  `blood-only` add-on set — the same routing `booking-deeplink.ts` uses to
 *  send a membership to the booking app's blood tab (AHD-9123), and the same
 *  reasoning behind `getCoverage()` reusing the blood-test coverage rule for
 *  `membership-*`. Without these three entries the add-ons step never
 *  appeared for a membership at all. */
const SERVICE_TO_PACKAGE_TYPE: Record<string, string> = {
  "core-mri": "mri",
  "advanced-mri": "mri",
  "pro-mri": "mri",
  "blood-test": "blood-only",
  "membership-basic": "blood-only",
  "membership-plus-1": "blood-only",
  "membership-plus-3": "blood-only",
}

/** Map insurance-checker service IDs → Supabase package human_readable_id.
 *  Used to drop add-ons already inside the package. The memberships point at
 *  `blood-test-only` because that is the panel they bundle. */
const SERVICE_TO_PACKAGE_ID: Record<string, string> = {
  "core-mri": "core",
  "advanced-mri": "advanced",
  "pro-mri": "pro",
  "blood-test": "blood-test-only",
  "membership-basic": "blood-test-only",
  "membership-plus-1": "blood-test-only",
  "membership-plus-3": "blood-test-only",
}

export interface Addon {
  id: string
  humanReadableId: string
  name: string
  /** What this visitor pays, in CHF: the member price when the selected
   *  service is a membership tier, the list price otherwise. Everything
   *  downstream — the running total, the reimbursement calc, the gate payload,
   *  the pro forma — reads this one number, so a member is quoted, reimbursed
   *  and invoiced on the price they will actually be charged. */
  price: number
  /** The public price. Equal to `price` unless a membership is selected, in
   *  which case the selector strikes it through. */
  listPrice: number
  packageTypes: string[]
}

/** An add-on as the catalogue holds it, before a service resolves which of the
 *  two prices applies. */
interface CatalogAddon {
  id: string
  humanReadableId: string
  name: string
  /** List price in CHF. */
  price: number
  /** Member price in CHF, absent when Stripe carries none for the product. */
  memberPrice?: number
  packageTypes: string[]
}

interface CachedProducts {
  addons: CatalogAddon[]
  /** component UUIDs included in each package (keyed by human_readable_id) */
  packageComponents: Map<string, Set<string>>
  fetchedAt: number
}

let cache: CachedProducts | null = null
const CACHE_TTL = 15 * 60 * 1000 // 15 min

async function fetchAllProducts(): Promise<CachedProducts> {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL) {
    return cache
  }

  try {
    // Same-origin proxy for the Supabase `get-products` edge function — a direct
    // browser fetch is blocked by its CORS preflight (AHD-8129).
    const res = await fetch("/api/insurance/addons")

    if (!res.ok) {
      captureException(
        new Error(`Supabase get-products failed: ${res.status}`),
        {
          url: res.url,
        }
      )
      return cache ?? { addons: [], packageComponents: new Map(), fetchedAt: 0 }
    }

    const json = await res.json()
    const products = json.products || json

    const addons: CatalogAddon[] = []
    const packageComponents = new Map<string, Set<string>>()

    for (const p of products) {
      // Collect package component sets (which product UUIDs are included)
      if (p.product_type === "package" && Array.isArray(p.components)) {
        packageComponents.set(p.human_readable_id, new Set(p.components))
      }

      // Collect add-on products
      if (p.product_type !== "standard") continue
      if (p.mandatory === true) continue
      if (p.status !== "active" || !p.active) continue
      if (EXCLUDE_IDS.has(p.human_readable_id)) continue
      if (!p.package_types || p.package_types.length === 0) continue

      const price = p.default_price?.unit_amount
      if (!price || price <= 0) continue

      // Stripe's `member_price` is the sole source of member pricing (same
      // field `lib/pricing.ts` reads for /pricing). Today every active add-on
      // carries one, but a new product can ship before its member price does,
      // so an absent one just means "no member rate for this add-on".
      const memberPrice = p.member_price?.unit_amount

      addons.push({
        id: p.id,
        humanReadableId: p.human_readable_id,
        name: p.name,
        price: price / 100,
        memberPrice:
          memberPrice && memberPrice > 0 ? memberPrice / 100 : undefined,
        packageTypes: p.package_types,
      })
    }

    // Sort by price ascending
    addons.sort((a, b) => a.price - b.price)

    cache = { addons, packageComponents, fetchedAt: Date.now() }
    return cache
  } catch (err) {
    // Per-user network failures (offline, ad blocker, navigated away) are
    // unactionable. HTTP errors are still reported above via the !res.ok branch.
    if (!isFetchNetworkError(err)) {
      captureException(
        new Error(
          `Supabase get-products fetch: ${(err as Error)?.message ?? err}`
        ),
        { source: "addons.fetchAllProducts", originalError: String(err) }
      )
    }
    return cache ?? { addons: [], packageComponents: new Map(), fetchedAt: 0 }
  }
}

/** Prefetch on module load */
let prefetchPromise: Promise<CachedProducts> | null = null
export function prefetchAddons(): void {
  if (!prefetchPromise) {
    prefetchPromise = fetchAllProducts()
  }
}

/**
 * Get add-ons compatible with the selected service.
 * Filters out add-ons already included in the package (from Supabase components).
 */
export async function getAddonsForService(
  serviceId: string,
  /** Whether a membership tier is selected, which is what earns the member
   *  rate. Passed in rather than inferred from `serviceId`: the two coexist
   *  once a member picks a check-up, so `serviceId` is then an MRI id and the
   *  membership lives in its own slot (coverage-form.tsx). */
  isMember = false
): Promise<Addon[]> {
  const packageType = SERVICE_TO_PACKAGE_TYPE[serviceId]
  if (!packageType) return []

  const { addons, packageComponents } = await fetchAllProducts()

  // Get component UUIDs included in this service's package
  const pkgId = SERVICE_TO_PACKAGE_ID[serviceId]
  const included = pkgId
    ? (packageComponents.get(pkgId) ?? new Set<string>())
    : new Set<string>()

  // Resolve the price ONCE, here, rather than threading the flag on through
  // the selector, the totals, panel-result, the gate payload and the pro
  // forma. Downstream code keeps reading `addon.price` and is correct for both
  // kinds of visitor.
  return addons
    .filter((addon) => {
      if (!addon.packageTypes.includes(packageType)) return false
      // Exclude add-ons already included in this package
      if (included.has(addon.id)) return false
      return true
    })
    .map(({ memberPrice, ...addon }) => ({
      ...addon,
      price: isMember && memberPrice != null ? memberPrice : addon.price,
      listPrice: addon.price,
    }))
}
